import React, { useEffect } from 'react';
import { ReactNode } from 'react';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  message: ReactNode;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
  isLoading?: boolean;
  subtitle?: string; // Nuevo: permite subtítulo opcional
  publicationTitle?: string; // Nuevo: título de la publicación
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  message,
  confirmText = 'Eliminar',
  cancelText = 'Cancelar',
  type = 'danger',
  isLoading = false,
  subtitle = 'Esta acción no se puede deshacer.',
  publicationTitle // Nuevo
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Iconos y colores según el tipo
  let icon, borderColor, bgColor, textColor, buttonColor, buttonHoverColor;
  if (type === 'info') {
    icon = (
      <svg className="w-7 h-7" fill="none" stroke="#44aaf6" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="#44aaf6" strokeWidth="2" fill="#e6f2fd" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01" />
      </svg>
    );
    borderColor = 'border-blue-200';
    bgColor = 'bg-blue-50';
    textColor = 'text-[#44aaf6]';
    buttonColor = 'bg-[#44aaf6]';
    buttonHoverColor = 'hover:bg-blue-600';
  } else {
    icon = (
      <svg className="w-7 h-7" fill="none" stroke="#ef4444" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
    );
    borderColor = 'border-red-200';
    bgColor = 'bg-red-50';
    textColor = 'text-[#ef4444]';
    buttonColor = 'bg-[#ef4444]';
    buttonHoverColor = 'hover:bg-red-600';
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Fondo oscuro */}
      <div className="absolute inset-0 bg-black bg-opacity-30 transition-opacity duration-300" style={{animation: 'fadeIn 0.3s'}} />
      {/* Modal */}
      <div className={`bg-white rounded-2xl shadow-xl p-7 max-w-xs w-full text-center flex flex-col items-center z-10 ${borderColor} font-futura relative border-2`}>
        {/* Botón cerrar */}
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 text-gray-400 ${type === 'info' ? 'hover:text-blue-400' : 'hover:text-red-400'} text-2xl font-bold focus:outline-none transition-colors duration-200`}
          aria-label="Cerrar"
          disabled={isLoading}
        >
          ×
        </button>
        {/* Icono */}
        <div className={`${bgColor} rounded-full p-2 mb-3 flex items-center justify-center shadow-sm ${borderColor}`}>
          {icon}
        </div>
        {/* Título principal */}
        <div className={`${textColor} font-semibold text-xl mb-1`} style={{letterSpacing: '0.01em'}}>{message}</div>
        {/* Título de la publicación, si existe */}
        {publicationTitle && (
          <div className="text-gray-800 font-medium text-base mb-2 mt-1 break-words" style={{fontSize: '1.08rem'}}>{publicationTitle}</div>
        )}
        {/* Subtítulo */}
        <div className="text-gray-700 mb-5 text-sm leading-relaxed">{subtitle}</div>
        {/* Botones */}
        <div className="flex flex-col gap-2 w-full">
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`w-full px-6 py-2.5 rounded-full ${buttonColor} text-white font-semibold text-lg ${buttonHoverColor} transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 border ${borderColor} disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Procesando...</span>
              </div>
            ) : (
              confirmText
            )}
          </button>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="w-full px-6 py-2 rounded-full border border-gray-300 text-gray-700 font-semibold text-base hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{letterSpacing: '0.08em'}}>
            {cancelText}
          </button>
        </div>
      </div>
      <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
    </div>
  );
};

export default ConfirmModal; 